import React, { Suspense, useState, useEffect } from "react"
import {
  BrowserRouter,
  Redirect,
  Route,
  Switch,
  useHistory,
} from "react-router-dom"
import { createBrowserHistory } from "history"
import { message } from "antd"
import {
  useStoreState,
  StoreProvider,
  useStoreActions,
  StateMapper,
} from "easy-peasy"
import { AppDataStore } from "./appData/types"
import store from "./appData/store"
import LoginWithRouter from "./components/LoginPage"
import ActivateAndLoginWithRouter from "./components/ActivateAndLoginPage"
import ClubLeagueLogin from "./components/ClubLeagueLogin"
import AdminApprovalAction from "./components/AdminApprovalAction"
import PasswordResetEmail from "./components/PasswordResetEmail"
import PasswordReset from "./components/PasswordReset"
import NotFound from "./components/NotFound"
import ActivateThisUser from "./components/ActivateThisUser"
import GlobalStyles from "./styles/GlobalStyles"
// Import directly (not the "./components/utils" barrel) to avoid a circular-import
// ordering issue that leaves these undefined under CJS/Jest.
import { gen, swimminglyApi } from "./components/utils/helperFunctions"
import { CustomEventEmitter } from "./components/utils/sharedDataTypes"
import CenterSpin from "./components/CenterSpin"
import RegisterParentForm from "./components/RegisterParentForm"
import ClubSwimmerRegistration from "./components/ClubSwimmerRegistration"
import { Wrapper } from "@googlemaps/react-wrapper";
import { googleMapsServerApiKey } from "./components/utils"
import EmailPreferencesPage from "./components/EmailPreferencesPage"
// Possibly only conditionally import TestRoute
// above when we are in development

import "./App.css"
import "./styles/nprogress.css"
import NationalChampsUserRegistration from "./components/NationalChampsUserRegistration"
export const seasonPrepModalListener = new CustomEventEmitter()

const InAppRoutesLazy = React.lazy(() => import("./components/InAppRoutes"))

declare global {
  interface Window {
    google: any
    _wq: any
  }
}

export interface ISeasonDetails {
  clubId: number
  seasonId: number
  clubName: string
  seasonSetupConfirmedBy: number | null
  seasonSetupConfirmedOn: string | null
  isSwimminglyCustomer: 0 | 1 | null
  corporatePaymentId: number | null
  clubPays: number | null
  parentPays: number | null
  seasonName: string
  startDate: string
  endDate: string
}

const AnyStoreProvider = StoreProvider as unknown as React.ComponentType<any>

function App() {
  const setAuthUser = useStoreActions((actions: AppDataStore) => actions.setAuthUser)
  const user = useStoreState((state: StateMapper<AppDataStore>) => state.user)
  const setUser = useStoreActions((actions: AppDataStore) => actions.setUser)
  const setAliasedUser = useStoreActions(
    (actions: AppDataStore) => actions.setAliasedUser,
  )
  const setImpersonateClub = useStoreActions(
    (actions: AppDataStore) => actions.setAliasedClub,
  )
  const acceptedCookies = useStoreState(
    (state: StateMapper<AppDataStore>) => state.acceptedCookies,
  )
  const setAcceptedCookies = useStoreActions(
    (actions: AppDataStore) => actions.setAcceptedCookies,
  )
  const windowHistory = createBrowserHistory()
  const history = useHistory()
  const [bootstrappingUser, setBootstrappingUser] = useState(true)

  useEffect(() => {
    if (user?.role === 2) message.success(`Welcome back ${user?.email}`)
  }, [user?.role, user?.email])

  useEffect(() => {
    let cancelled = false

    swimminglyApi
      .get(gen("/api/whoami"))
      .then((data) => {
        if (cancelled) return

        if (data && data.acceptedCookies === true) {
          setAcceptedCookies(true)
        }

        if (!data || !data.user || !data.acceptedCookies) {
          setAuthUser(null)
          setUser(null)
          setAliasedUser(null)
          setImpersonateClub(null)

          const shouldShowLogin = (
            window.location.pathname !== "/registerUser" &&
            window.location.pathname !== "/registerUser/2021NationalChamps" &&
            window.location.pathname !== "/registerParent" &&
            window.location.pathname !== "/passwordReset" &&
            !/^\/email-preferences/.test(window.location.pathname) &&
            !/^\/outside\//.test(window.location.pathname) &&
            !/^\/password\/reset\//.test(window.location.pathname) &&
            !/^\/activate\//.test(window.location.pathname) &&
            !/^\/activateThisUser\//.test(window.location.pathname) &&
            !/^\/approveAdminRequest\//.test(window.location.pathname) &&
            !/^\/rejectAdminRequest\//.test(window.location.pathname) &&
            !/^\/clubs\/register\//.test(window.location.pathname)
          )

          if (shouldShowLogin) {
            const intendedPath = window.location.pathname + window.location.search
            history.push({
              pathname: "/login",
              state: { targetUrl: intendedPath !== "/login" ? intendedPath : undefined },
            })
          }
        } else {
          const pathName = window.location.pathname
          setAuthUser(data.user)
          setAliasedUser(data.aliasedUser || null)
          setUser(data.effectiveUser || data.aliasedUser || data.user)
          if (/^\/outside\//.test(`${pathName}`)) {
            //establishes the convention that any front end route that begins with /outside will authenticate and then push the route onto the history stack
            if (data.club) {
              setImpersonateClub(data.club)
            }
            history.push(pathName)
          } else if (/^\/activate\//.test(`${pathName}`)) {
            // If the user visits an activation link while already logged in,
            // do NOT redirect to home; allow activation route to run.
            history.push(pathName)
          } else if (
            /^\/approveAdminRequest\//.test(pathName) ||
            /^\/rejectAdminRequest\//.test(pathName)
          ) {
            // Do NOT redirect; let the approval action component handle the token.
            if (data.club) {
              setImpersonateClub(data.club)
            }
          } else if (data.club) {
            setImpersonateClub(data.club)
          } else {
            history.push("/")
          }
        }
      })
      .catch(() => {
        // On error, treat as unauthenticated; outer guard will handle redirects
      })
      .finally(() => {
        if (!cancelled) {
          setBootstrappingUser(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [
    history,
    setAcceptedCookies,
    setAuthUser,
    setAliasedUser,
    setImpersonateClub,
    setUser,
  ])
  
  if (bootstrappingUser) {
    return (
      <div style={{ paddingTop: "200px", margin: "auto" }}>
        <CenterSpin />
      </div>
    )
  }

  if (
    !user &&
    window.location.pathname !== "/" &&
    window.location.pathname !== "/login" &&
    window.location.pathname !== "/registerUser/2021NationalChamps" &&
    window.location.pathname !== "/registerUser" &&
    window.location.pathname !== "/registerParent" &&
    window.location.pathname !== "/passwordReset" &&
    !/^\/email-preferences/.test(window.location.pathname) &&
    !/^\/outside\//.test(window.location.pathname) &&
    !/^\/password\/reset\//.test(window.location.pathname) &&
    !/^\/activate\//.test(window.location.pathname) &&
    !/^\/activateThisUser\//.test(window.location.pathname) &&
    !/^\/approveAdminRequest\//.test(window.location.pathname) &&
    !/^\/rejectAdminRequest\//.test(window.location.pathname) &&
    !/^\/clubs\/register\//.test(window.location.pathname)
  ) {
    const intendedPath = window.location.pathname + window.location.search
    return <Redirect to={{ pathname: "/login", state: { targetUrl: intendedPath !== "/login" ? intendedPath : undefined } }} />
  }

  return (
    <div>
      {user && Array.isArray(user.pendingAdminRequests) && user.pendingAdminRequests.length > 0 && (
        <div style={{ background: "#fff3cd", borderBottom: "1px solid #ffc107", padding: "12px 24px", textAlign: "center" }}>
          {user.pendingAdminRequests.map((req) => {
            const orgName = req.clubName || req.leagueName || "your team"
            const roleLabel = req.requestedRole === 3 ? "League Admin" : "Club Admin"
            return (
              <span key={req.id} style={{ color: "#856404" }}>
                Your request to join <strong>{orgName}</strong> as a <strong>{roleLabel}</strong> is pending approval from existing admins.
              </span>
            )
          })}
        </div>
      )}
      <Switch>
        {/* Public Routes */}
        <Route exact path="/" key="home" render={() => <LoginWithRouter /> } />
        <Route
          exact
          path="/login"
          key="login"
          render={() => <LoginWithRouter />}
        />
        <Route
          path="/activate/:activationToken"
          key="home"
          render={(props) => {
            const clubToken = new URLSearchParams(props.location.search).get("clubToken")
            return (
              <ActivateAndLoginWithRouter
                {...props}
                activationToken={props.match.params.activationToken}
                clubToken={clubToken}
              />
            )
          }}
        />
        <Route
          exact
          path="/registerUser/2021NationalChamps"
          key="registerUser"
          render={() => {
            return <NationalChampsUserRegistration />
          }}
        />
        <Route
          exact
          path="/registerUser"
          key="registerUser"
          render={(props) => {
            return (
              <ClubLeagueLogin
                {...props}
                initialLeagueId={null}
                initialClubId={null}
                initialEmail={null}
                currentStep={null}
                active={false}
              />
            )
          }}
        />
        <Route
          exact
          path="/registerParent"
          key="registerParent"
          render={() => {
            return <RegisterParentForm />
          }}
        />
        <Route
          exact
          path="/registerUser/:leagueId/:clubId/:email"
          key="registerUser2"
          render={(props) => {
            let curStep: string = "3a"
            let leagueId: string = props.match.params.leagueId
            let clubId: string = props.match.params.clubId
            let email: string = props.match.params.email
            let active: boolean = true
            if (leagueId.toLowerCase() === "null") {
              curStep = "3b"
            }
            if (clubId.toLowerCase() === "null") {
              curStep = "3a"
            }
            return (
              <ClubLeagueLogin
                {...props}
                initialLeagueId={parseInt(leagueId || "0")}
                initialClubId={parseInt(clubId || "0")}
                initialEmail={email}
                currentStep={curStep}
                active={active}
              />
            )
          }}
        />
  
        <Route
          exact
          path="/activateThisUser/:userId"
          key="activateThisUser"
          render={(props) => {
            let userIdString: string = props.match.params.userId
            let userId: number | null
            if (!userIdString || userIdString) {
              userId = null
            } else if (!/^[0-9]+$/.test(userIdString)) {
              userId = null
            } else {
              userId = parseInt(userIdString)
            }
            return <ActivateThisUser userId={userId} />
          }}
        />
        <Route
          exact
          path="/approveAdminRequest/:token"
          key="approveAdminRequest"
          render={() => <AdminApprovalAction action="approve" />}
        />
        <Route
          exact
          path="/rejectAdminRequest/:token"
          key="rejectAdminRequest"
          render={() => <AdminApprovalAction action="reject" />}
        />
        <Route
          exact
          path="/passwordReset"
          key="passwordReset"
          render={() => {
            return <LoginWithRouter />
          }}
        />
        <Route
          exact
          path="/email-preferences"
          key="emailPreferences"
          render={() => <EmailPreferencesPage />}
        />
        <Route
          exact
          path="/password/reset/:token"
          key="passwordReset2"
          render={(props) => {
            let token: string = props.match.params.token
            return <PasswordReset token={token} />
          }}
        />
        <Route
          exact
          path="/clubs/register/:superSecretToken"
          key="superSecretLink"
          render={(props) => {
            const token: string = props.match.params.superSecretToken
            return <ClubSwimmerRegistration token={token} />
          }}
        />

        <Route 
          path="/outside/declarations/club/:clubId/meetLineUp/:meetId"
          render={(props: any) => {
            const redirectURL = `/app/parent/viewMeetLineUp/${props.match.params.meetId}/${props.match.params.clubId}`
            return  user ? <Redirect to={redirectURL} /> : <LoginWithRouter targetURL={redirectURL} />
          }}
          />

        <Route
          path="/app"
          render={() => (
            <Wrapper apiKey={googleMapsServerApiKey} libraries={["places"]}>
              <Suspense
                fallback={
                  <div style={{ paddingTop: "200px", margin: "auto" }}>
                    <CenterSpin />
                  </div>
                }
              >
                <InAppRoutesLazy />
              </Suspense>
            </Wrapper>
          )}
        />

        <Route component={NotFound} />
      </Switch>
      
    </div>
  )
}

function RouteAnalytics() {
  const history = useHistory()

  useEffect(() => {
    const unlisten = history.listen((location) => {
      const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID
      if (!measurementId) return

      const gtag = (window as any).gtag as
        | ((...args: any[]) => void)
        | undefined
      if (!gtag) return

      gtag("event", "page_view", {
        page_path: location.pathname + location.search,
      })
    })

    // initial page_view on first mount
    const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID
    const gtag = (window as any).gtag as
      | ((...args: any[]) => void)
      | undefined
    if (measurementId && gtag) {
      const { pathname, search } = window.location
      gtag("event", "page_view", {
        page_path: pathname + search,
      })
    }

    return () => {
      unlisten()
    }
  }, [history])

  return null
}

export default function AppWithData() {
  return ( 
    <AnyStoreProvider store={store}>
      <BrowserRouter>
        <GlobalStyles />
        <App />
      </BrowserRouter>
    </AnyStoreProvider>
  )
}
